import { s } from '@/theme/tokens';
import { DeltaWithIndicator } from '@/ui/composites/deltaIndicator';
import { Box, Surface, Text } from '@/ui/primitives';
import { Chip } from '../primitives/Chip';
import { getDeltaTextColor } from './table/deltaColors';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement, ReactNode } from 'react';

type MetricCardProps = {
  status: ReactNode;
  statusTone?: 'success' | 'warning' | 'error' | 'info' | 'brown' | 'primary' | 'default';
  count: ReactNode;
  percentage?: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'positive' | 'negative' | 'neutral';
  surfaceSx?: SxProps<Theme>;
};

const resolveDeltaColor = (muiTheme: Theme, tone: MetricCardProps['deltaTone']): string => {
  if (tone === 'positive') return getDeltaTextColor(muiTheme, true);
  if (tone === 'negative') return getDeltaTextColor(muiTheme, false);
  return muiTheme.palette.text.secondary;
};

const resolveDeltaIndicatorFromValue = (
  delta: string | number,
): { symbol: string; visible: boolean } => {
  if (typeof delta === 'number') {
    if (delta > 0) return { symbol: '▲', visible: true };
    if (delta < 0) return { symbol: '▼', visible: true };
    return { symbol: '', visible: false };
  }
  const trimmed = delta.trim();
  if (trimmed.startsWith('+')) return { symbol: '▲', visible: true };
  if (trimmed.startsWith('-')) return { symbol: '▼', visible: true };
  return { symbol: '', visible: false };
};

const resolveDeltaValue = (delta?: ReactNode): ReactNode => {
  if (delta === 0 || delta === '0' || delta === '0%') {
    return '-';
  }
  if (delta === '-') return '-';
  if (typeof delta === 'string' || typeof delta === 'number') {
    const indicator = resolveDeltaIndicatorFromValue(delta);
    if (!indicator.visible) return delta;
    return <DeltaWithIndicator label={delta} isPositive={indicator.symbol === '▲'} />;
  }
  return delta;
};

export const MetricCard = ({
  status,
  statusTone = 'default',
  count,
  percentage,
  delta,
  deltaTone = 'neutral',
  surfaceSx,
}: MetricCardProps): ReactElement => {
  const isTotal = typeof status === 'string' && status.trim().toLowerCase() === 'total';
  const showPercentage =
    !isTotal && percentage !== null && percentage !== undefined && percentage !== '';
  const showDelta = delta !== null && delta !== undefined;
  const gridColumns = isTotal
    ? showDelta
      ? '1fr 1fr 1fr'
      : '1fr 1fr'
    : showDelta
      ? '1fr 1fr 1fr 1fr'
      : '1fr 1fr 1fr';
  const mergedSurfaceSx: SxProps<Theme> = surfaceSx
    ? ([
        { backgroundColor: 'background.paper' },
        ...(Array.isArray(surfaceSx) ? surfaceSx : [surfaceSx]),
      ] as SxProps<Theme>)
    : ({ backgroundColor: 'background.paper' } as SxProps<Theme>);

  return (
    <Surface padding="lg" sx={mergedSurfaceSx}>
      <Box
        sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: {
            xs: gridColumns,
            sm: 'none',
          },
          alignItems: 'center',
          columnGap: { xs: s('sm'), sm: s('md') },
          rowGap: { xs: s('xs'), sm: s('none') },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-start' } }}>
          <Chip label={status} tone={statusTone} transform="uppercase" />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: {
              xs: isTotal && !showDelta ? 'flex-end' : 'center',
              sm: 'flex-start',
            },
            textAlign: {
              xs: isTotal && !showDelta ? 'right' : 'center',
              sm: 'left',
            },
          }}
        >
          <Text role="sectionTitle">{count}</Text>
        </Box>
        {isTotal ? null : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: {
                xs: !showDelta ? 'flex-end' : 'center',
                sm: 'flex-start',
              },
              textAlign: { xs: !showDelta ? 'right' : 'center', sm: 'left' },
            }}
          >
            {showPercentage ? <Text role="bodyMuted">{percentage}</Text> : null}
          </Box>
        )}
        {showDelta ? (
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            <Text
              role="bodyMuted"
              sx={(muiTheme) => ({
                color: resolveDeltaColor(muiTheme, deltaTone),
                fontSize: muiTheme.typography.body.fontSize,
              })}
            >
              {resolveDeltaValue(delta)}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Surface>
  );
};
